import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Send, Linkedin as LinkedinIcon, Instagram } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { profileData } from '../mock';

// Contact form is now handled by a Netlify Function at /.netlify/functions/contact
// (frontend/netlify/functions/contact.mjs). No backend env var needed.
const CONTACT_ENDPOINT = '/.netlify/functions/contact';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send message');
      }
      toast({
        title: 'Message sent',
        description: "Thank you for reaching out. I'll get back to you soon.",
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast({
        title: "Couldn't send message",
        description: err.message || 'Please try again later or email mezhercarol@gmail.com directly.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-12 bg-midnight-blue overflow-hidden">
      {/* Same pattern as hero — low opacity backdrop */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage: 'url(/hero-pattern.png)',
          backgroundRepeat: 'repeat',
          backgroundSize: '760px auto',
          backgroundPosition: 'top left',
        }}
        data-testid="contact-pattern-backdrop"
      />
      <div className="container mx-auto px-8 md:px-16 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Two-column layout */}
          <div className="grid lg:grid-cols-5 gap-10">
            {/* Left - info card (white) */}
            <div className="lg:col-span-2">
              <div className="bg-white text-charcoal p-8 rounded-sm h-full shadow-2xl relative overflow-hidden">
                {/* Decorative accent */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-terracotta/32 rounded-full blur-2xl"></div>
                <div className="relative z-10">
                  <h3 className="font-playfair font-bold text-3xl mb-5 text-whale-blue">Reach Out</h3>
                  <p className="text-charcoal/80 leading-relaxed mb-6">
                    Let's bring your project to life.
                  </p>
                  
                  <div className="space-y-5">
                    <div>
                      <h4 className="text-xs tracking-[0.3em] uppercase text-whale-blue mb-3 font-montserrat font-bold">
                        Connect
                      </h4>
                      <a
                        href={profileData.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-whale-blue hover:text-midnight-blue transition-colors group bg-terracotta/17 hover:bg-terracotta/27 p-3 rounded-sm"
                      >
                        <LinkedinIcon size={22} />
                        <span className="font-montserrat font-semibold">LinkedIn Profile</span>
                      </a>
                      <a
                        href="https://www.instagram.com/carol_mezher"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 mt-2 text-whale-blue hover:text-midnight-blue transition-colors group bg-terracotta/17 hover:bg-terracotta/27 p-3 rounded-sm"
                      >
                        <Instagram size={22} />
                        <span className="font-montserrat font-semibold">Instagram Profile</span>
                      </a>
                    </div>

                    {/* Availability Info */}
                    <div className="bg-terracotta/17 border-l-4 border-terracotta p-5 rounded-sm mt-6">
                      <div className="space-y-2">
                        <p className="text-midnight-blue font-montserrat text-sm font-bold flex items-center gap-2">
                          📍 Based in Paris, working globally
                        </p>
                        <p className="text-charcoal/80 font-montserrat text-xs">
                          Available for international projects
                        </p>
                        <p className="text-charcoal/80 font-montserrat text-xs">
                          Open to collaborations across formats
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-xs tracking-[0.3em] uppercase text-white/70 mb-2 block font-montserrat font-bold">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-white/20 border-2 border-white/40 focus-visible:ring-whale-blue focus-visible:border-whale-blue rounded-sm text-white placeholder:text-white/70"
                      placeholder=""
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-xs tracking-[0.3em] uppercase text-white/70 mb-2 block font-montserrat font-bold">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-white/20 border-2 border-white/40 focus-visible:ring-whale-blue focus-visible:border-whale-blue rounded-sm text-white placeholder:text-white/70"
                      placeholder=""
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject" className="text-xs tracking-[0.3em] uppercase text-white/70 mb-2 block font-montserrat font-bold">
                    Subject *
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="bg-white/20 border-2 border-white/40 focus-visible:ring-whale-blue focus-visible:border-whale-blue rounded-sm text-white placeholder:text-white/70"
                    placeholder=""
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-xs tracking-[0.3em] uppercase text-white/70 mb-2 block font-montserrat font-bold">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="bg-white/20 border-2 border-white/40 focus-visible:ring-whale-blue focus-visible:border-whale-blue resize-none rounded-sm text-white placeholder:text-white/70"
                    placeholder=""
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border-2 border-white/40 hover:border-white/60 px-6 py-3 text-base tracking-wide font-montserrat font-medium transition-smooth rounded-sm shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending…' : 'Send Message'}
                  <Send className="ml-2" size={18} />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;